export type AnalyticsParams = Record<string, string | number | boolean | undefined>;

type PostHogCaptureOptions = {
  send_instantly?: boolean;
  transport?: "XHR" | "fetch" | "sendBeacon";
};

declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void;
    fbq?: (...args: unknown[]) => void;
    posthog?: {
      capture?: (
        eventName: string,
        properties?: AnalyticsParams,
        options?: PostHogCaptureOptions,
      ) => void;
    };
  }
}

const ATTRIBUTION_KEYS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_id",
  "utm_content",
  "utm_term",
  "utm_source_platform",
  "utm_creative_format",
  "utm_marketing_tactic",
  "gclid",
  "gbraid",
  "wbraid",
  "fbclid",
  "campaign_id",
  "adset_id",
  "ad_id",
  "placement",
  "ad_group_id",
  "creative_id",
  "match_type",
  "network",
  "device",
] as const;

const SESSION_ATTRIBUTION_KEY = "pv_campaign_attribution";
const PERSISTED_PAID_TOUCH_KEY = "pv_persisted_paid_touch";
const PERSISTED_PAID_TOUCH_TTL_MS = 7 * 24 * 60 * 60 * 1000;
const FIRST_TOUCH_KEY = "pv_first_touch_attribution";
const LAST_TOUCH_KEY = "pv_last_touch_attribution";
const LANDING_CONTEXT_KEY = "pv_landing_context";
const GOOGLE_CONSENT_STORAGE_KEY = "pv_google_consent_v2";
const GA4_MEASUREMENT_ID = "G-Q2H5ETF15X";
const recentEvents = new Map<string, number>();

type StoredTouch = {
  captured_at: number;
  attribution: AnalyticsParams;
  landing: AnalyticsParams;
};

type StoredConsent = {
  analytics?: boolean;
  ads?: boolean;
};

const readStorage = <T,>(storage: Storage, key: string): T | null => {
  try {
    const raw = storage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : null;
  } catch {
    return null;
  }
};

const writeStorage = (storage: Storage, key: string, value: unknown) => {
  try {
    storage.setItem(key, JSON.stringify(value));
  } catch {
    // Analytics storage must never interfere with the visitor experience.
  }
};

const removeStorage = (storage: Storage, key: string) => {
  try {
    storage.removeItem(key);
  } catch {
    // Analytics storage must never interfere with the visitor experience.
  }
};

const readSessionObject = (key: string): AnalyticsParams =>
  typeof window === "undefined"
    ? {}
    : readStorage<AnalyticsParams>(window.sessionStorage, key) || {};

const writeSessionObject = (key: string, value: AnalyticsParams) => {
  if (typeof window !== "undefined") writeStorage(window.sessionStorage, key, value);
};

const stringValue = (value: AnalyticsParams[string]) =>
  typeof value === "string" ? value.trim() : value === undefined ? "" : String(value);

const getHostname = (url: string) => {
  if (!url) return "";
  try {
    return new URL(url).hostname.toLowerCase().replace(/^www\./, "");
  } catch {
    return "";
  }
};

const readCurrentAttribution = (): AnalyticsParams => {
  if (typeof window === "undefined") return {};

  const search = new URLSearchParams(window.location.search);
  return ATTRIBUTION_KEYS.reduce<AnalyticsParams>((params, key) => {
    const value = search.get(key);
    if (value) params[key] = value;
    return params;
  }, {});
};

const normalizeAttribution = (attribution: AnalyticsParams): AnalyticsParams => {
  if (typeof window === "undefined") return attribution;

  const search = new URLSearchParams(window.location.search);
  const legacyAdGroupId = search.get("adgroup_id");
  if (!attribution.ad_group_id && legacyAdGroupId) {
    return { ...attribution, ad_group_id: legacyAdGroupId };
  }

  return attribution;
};

const isNewCampaignTouch = (params: AnalyticsParams) =>
  Boolean(
    params.gclid ||
      params.gbraid ||
      params.wbraid ||
      params.fbclid ||
      params.utm_source ||
      params.utm_campaign,
  );

const isPaidTouch = (params: AnalyticsParams) => {
  const source = stringValue(params.utm_source).toLowerCase();
  const medium = stringValue(params.utm_medium).toLowerCase();

  return Boolean(
    params.gclid ||
      params.gbraid ||
      params.wbraid ||
      params.fbclid ||
      /cpc|ppc|paid[_-]?search|paid[_-]?social|social[_-]?paid|sem/.test(medium) ||
      /^(meta|facebook|instagram|fb|ig)$/.test(source),
  );
};

const currentLandingContext = (): AnalyticsParams => {
  if (typeof window === "undefined") return {};

  const referrer = document.referrer || "";
  const domain = getHostname(referrer);
  const current = window.location.hostname.toLowerCase().replace(/^www\./, "");
  const external = Boolean(domain && domain !== current);

  return {
    landing_referring_domain: external ? domain : "direct",
    ...(external ? { landing_referrer: referrer } : {}),
  };
};

const captureLandingContext = (): AnalyticsParams => {
  const stored = readSessionObject(LANDING_CONTEXT_KEY);
  if (Object.keys(stored).length) return stored;

  const context = currentLandingContext();
  writeSessionObject(LANDING_CONTEXT_KEY, context);
  return context;
};

const readPersistedPaidTouch = (): StoredTouch | null => {
  if (typeof window === "undefined") return null;

  const touch = readStorage<StoredTouch>(window.localStorage, PERSISTED_PAID_TOUCH_KEY);
  if (!touch?.captured_at || Date.now() - touch.captured_at > PERSISTED_PAID_TOUCH_TTL_MS) {
    removeStorage(window.localStorage, PERSISTED_PAID_TOUCH_KEY);
    return null;
  }

  return touch;
};

const consentSnapshot = (): AnalyticsParams => {
  if (typeof window === "undefined") return {};

  const stored = readStorage<StoredConsent>(window.localStorage, GOOGLE_CONSENT_STORAGE_KEY);
  return {
    analytics_storage_state:
      typeof stored?.analytics === "boolean" ? (stored.analytics ? "granted" : "denied") : "unset",
    ad_storage_state:
      typeof stored?.ads === "boolean" ? (stored.ads ? "granted" : "denied") : "unset",
  };
};

const classify = (attribution: AnalyticsParams, landing: AnalyticsParams): AnalyticsParams => {
  const source = stringValue(attribution.utm_source);
  const medium = stringValue(attribution.utm_medium);
  const normalizedSource = source.toLowerCase();
  const normalizedMedium = medium.toLowerCase();
  const domain = stringValue(landing.landing_referring_domain).toLowerCase();
  const clickType = attribution.gclid
    ? "gclid"
    : attribution.gbraid
      ? "gbraid"
      : attribution.wbraid
        ? "wbraid"
        : "";

  if (clickType) {
    return {
      ...landing,
      attribution_source: "google_ads",
      attribution_medium: medium || "paid",
      attribution_channel: /cpc|ppc|paid[_-]?search|sem/.test(normalizedMedium)
        ? "paid_search"
        : "paid_google",
      attribution_method: "google_click_id",
      attribution_click_id_type: clickType,
    };
  }

  const meta = /^(meta|facebook|instagram|fb|ig)$/.test(normalizedSource);
  if (source) {
    let channel = "campaign";
    if (/cpc|ppc|paid[_-]?search|sem/.test(normalizedMedium)) channel = "paid_search";
    else if (/paid[_-]?social|social[_-]?paid/.test(normalizedMedium)) channel = "paid_social";
    else if (/organic[_-]?social/.test(normalizedMedium)) channel = "organic_social";
    else if (/^organic$/.test(normalizedMedium)) channel = "organic";
    else if (/social/.test(normalizedMedium)) channel = "social";
    else if (/referral/.test(normalizedMedium)) channel = "referral";
    else if (/email/.test(normalizedMedium)) channel = "email";

    return {
      ...landing,
      attribution_source: meta
        ? normalizedSource === "instagram" || normalizedSource === "ig"
          ? "instagram"
          : normalizedSource === "facebook" || normalizedSource === "fb"
            ? "facebook"
            : "meta"
        : source,
      attribution_medium: medium || "campaign",
      attribution_channel: channel,
      attribution_method: "utm",
    };
  }

  if (attribution.fbclid) {
    return {
      ...landing,
      attribution_source: domain.includes("instagram") ? "instagram" : "facebook",
      attribution_medium: "paid_social",
      attribution_channel: "paid_social",
      attribution_method: "facebook_click_id",
    };
  }

  if (domain && domain !== "direct") {
    if (/(^|\.)google\./.test(domain) || domain.includes("google.com")) {
      return {
        ...landing,
        attribution_source: "google",
        attribution_medium: "organic",
        attribution_channel: "organic_search",
        attribution_method: "referrer",
      };
    }
    if (domain.includes("instagram.com")) {
      return {
        ...landing,
        attribution_source: "instagram",
        attribution_medium: "organic_social",
        attribution_channel: "organic_social",
        attribution_method: "referrer",
      };
    }
    if (domain.includes("facebook.com") || domain.includes("l.facebook.com")) {
      return {
        ...landing,
        attribution_source: "facebook",
        attribution_medium: "organic_social",
        attribution_channel: "organic_social",
        attribution_method: "referrer",
      };
    }
    if (
      domain.includes("bing.com") ||
      domain.includes("yahoo.com") ||
      domain.includes("duckduckgo.com")
    ) {
      return {
        ...landing,
        attribution_source: domain.split(".")[0],
        attribution_medium: "organic",
        attribution_channel: "organic_search",
        attribution_method: "referrer",
      };
    }

    return {
      ...landing,
      attribution_source: domain,
      attribution_medium: "referral",
      attribution_channel: "referral",
      attribution_method: "referrer",
    };
  }

  return {
    ...landing,
    attribution_source: "direct",
    attribution_medium: "none",
    attribution_channel: "direct",
    attribution_method: "direct",
  };
};

const touchProps = (prefix: string, touch: StoredTouch | null): AnalyticsParams => {
  if (!touch) return {};

  const normalized = classify(touch.attribution, touch.landing);
  const age = Math.max(0, Math.round((Date.now() - touch.captured_at) / 1000));

  return {
    [`${prefix}_source`]: normalized.attribution_source,
    [`${prefix}_medium`]: normalized.attribution_medium,
    [`${prefix}_channel`]: normalized.attribution_channel,
    [`${prefix}_campaign`]: touch.attribution.utm_campaign,
    [`${prefix}_content`]: touch.attribution.utm_content,
    [`${prefix}_captured_at`]: new Date(touch.captured_at).toISOString(),
    [`${prefix}_age_seconds`]: age,
  };
};

export const captureAttribution = (): AnalyticsParams => {
  if (typeof window === "undefined") return {};

  const current = normalizeAttribution(readCurrentAttribution());
  // Persist the ORIGINAL landing context on the very first call of the session so later
  // in-app navigations (where document.referrer is internal) keep the true entry source.
  const landing = captureLandingContext();
  const sessionStored = readSessionObject(SESSION_ATTRIBUTION_KEY);
  const persistedPaidTouch = readPersistedPaidTouch();
  const campaign = isNewCampaignTouch(current);

  let attribution: AnalyticsParams;
  if (campaign) attribution = current;
  else if (Object.keys(sessionStored).length) attribution = sessionStored;
  else attribution = persistedPaidTouch?.attribution || {};

  if (Object.keys(attribution).length) writeSessionObject(SESSION_ATTRIBUTION_KEY, attribution);

  const external = landing.landing_referring_domain !== "direct";
  if (campaign || external) {
    const touch: StoredTouch = {
      captured_at: Date.now(),
      attribution: current,
      landing,
    };

    const first = readStorage<StoredTouch>(window.localStorage, FIRST_TOUCH_KEY);
    if (!first) writeStorage(window.localStorage, FIRST_TOUCH_KEY, touch);
    writeStorage(window.localStorage, LAST_TOUCH_KEY, touch);

    if (campaign && isPaidTouch(current)) {
      writeStorage(window.localStorage, PERSISTED_PAID_TOUCH_KEY, touch);
    }
  }

  return attribution;
};

const withAttribution = (params: AnalyticsParams = {}): AnalyticsParams => {
  const attribution = captureAttribution();
  const landing = captureLandingContext();
  const combined = { ...attribution, ...params };
  const current = classify(combined, landing);

  if (typeof window === "undefined") return { ...combined, ...current };

  const first = readStorage<StoredTouch>(window.localStorage, FIRST_TOUCH_KEY);
  const last = readStorage<StoredTouch>(window.localStorage, LAST_TOUCH_KEY);

  return {
    ...combined,
    ...current,
    ...touchProps("first_touch", first),
    ...touchProps("last_touch", last),
  };
};

const shouldSkipDuplicate = (
  channel: "ga4" | "meta",
  eventName: string,
  params: AnalyticsParams,
) => {
  if (eventName !== "outbound_booking_click" && eventName !== "OutboundBookingClick") return false;

  const key = [
    channel,
    eventName,
    params.property_name || "unknown",
    params.platform || "unknown",
    params.page_path || (typeof window !== "undefined" ? window.location.pathname : ""),
  ].join(":");
  const now = Date.now();
  const previous = recentEvents.get(key) || 0;
  recentEvents.set(key, now);
  return now - previous < 800;
};

const trackPostHogEvent = (
  eventName: string,
  params: AnalyticsParams,
  options?: PostHogCaptureOptions,
) => {
  if (typeof window === "undefined") return;

  if (eventName === "page_view") {
    window.posthog?.capture?.("$pageview", { ...params, $current_url: window.location.href }, options);
    return;
  }

  window.posthog?.capture?.(eventName, params, options);
};

export const trackEvent = (eventName: string, params: AnalyticsParams = {}) => {
  if (typeof window === "undefined") return;

  const enriched = withAttribution(params);
  if (shouldSkipDuplicate("ga4", eventName, enriched)) return;

  window.gtag?.("event", eventName, enriched);
  trackPostHogEvent(eventName, enriched);
};

export const trackOutboundBookingEvent = (params: AnalyticsParams = {}) => {
  if (typeof window === "undefined") return;

  const enriched = withAttribution({ ...params, ...consentSnapshot() });
  if (shouldSkipDuplicate("ga4", "outbound_booking_click", enriched)) return;

  window.gtag?.("event", "outbound_booking_click", {
    ...enriched,
    send_to: GA4_MEASUREMENT_ID,
    transport_type: "beacon",
    event_timeout: 800,
    event_callback: () => {
      // The callback intentionally does not control navigation. Outbound links open in a
      // separate tab; beacon transport is used so mobile browsers can safely background
      // the source page without dropping the booking-intent hit.
    },
  });

  trackPostHogEvent("outbound_booking_click", enriched, {
    send_instantly: true,
    transport: "sendBeacon",
  });
};

export const trackMetaEvent = (eventName: string, params: AnalyticsParams = {}) => {
  if (typeof window === "undefined") return;

  const enriched = withAttribution(params);
  if (shouldSkipDuplicate("meta", eventName, enriched)) return;
  window.fbq?.("track", eventName, enriched);
};

export const trackMetaCustomEvent = (eventName: string, params: AnalyticsParams = {}) => {
  if (typeof window === "undefined") return;

  const enriched = withAttribution(params);
  if (shouldSkipDuplicate("meta", eventName, enriched)) return;
  window.fbq?.("trackCustom", eventName, enriched);
};
